import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LangContext";
import { useSettings } from "@/contexts/SettingsContext";
import { useSiteCopy } from "@/hooks/useSiteCopy";
import SocialIcons from "@/components/SocialIcons";
import { Phone, Mail } from "lucide-react";

export default function Footer() {
  const { t } = useLang();
  const { settings } = useSettings();
  const copy = useSiteCopy();
  const year = new Date().getFullYear();

  return (
    <footer
      data-testid="site-footer"
      className="border-t border-edge bg-bg-surface mt-32"
    >
      <div className="max-w-[1440px] mx-auto px-6 sm:px-12 lg:px-16 py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="font-display text-3xl text-ink">C—Carpenter</div>
            <p className="text-eyebrow text-brand mt-2">Bespoke Joinery &amp; Construction Ltd</p>
            <p className="mt-4 text-ink-muted text-sm leading-relaxed max-w-sm">
              {copy("footer_tagline", "footer.tagline")}
            </p>

            <div className="mt-6 space-y-2 text-sm">
              {settings.phone && (
                <a
                  data-testid="footer-phone"
                  href={`tel:${settings.phone.replace(/\s/g, "")}`}
                  className="flex items-center gap-3 text-ink hover:text-brand transition-colors"
                >
                  <Phone size={14} className="text-brand" /> {settings.phone}
                </a>
              )}
              {settings.email && (
                <a
                  data-testid="footer-email"
                  href={`mailto:${settings.email}`}
                  className="flex items-center gap-3 text-ink hover:text-brand transition-colors"
                >
                  <Mail size={14} className="text-brand" /> {settings.email}
                </a>
              )}
            </div>

            <div className="mt-8">
              <SocialIcons testIdPrefix="footer-social" />
            </div>

            <p className="mt-6 text-eyebrow text-ink-dim">
              c-carpenter.co.uk · London
            </p>
          </div>

          <div className="md:col-span-3">
            <h4 className="text-eyebrow text-ink-muted mb-5">
              {t("footer.navigate")}
            </h4>
            <ul className="space-y-3 text-sm">
              <li><Link to="/services" className="text-ink hover:text-brand transition-colors">{t("nav.services")}</Link></li>
              <li><Link to="/portfolio" className="text-ink hover:text-brand transition-colors">{t("nav.portfolio")}</Link></li>
              <li><Link to="/gallery" className="text-ink hover:text-brand transition-colors">{t("nav.gallery")}</Link></li>
              <li><Link to="/about" className="text-ink hover:text-brand transition-colors">{t("nav.about")}</Link></li>
              <li><Link to="/consultations" className="text-ink hover:text-brand transition-colors">{t("nav.consultations")}</Link></li>
              <li><Link to="/contact" className="text-ink hover:text-brand transition-colors">{t("nav.contact")}</Link></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="text-eyebrow text-ink-muted mb-5">
              {t("footer.legal")}
            </h4>
            <ul className="space-y-3 text-sm">
              <li className="text-ink-muted">{t("footer.privacy")}</li>
              <li className="text-ink-muted">{t("footer.terms")}</li>
              <li>
                <Link
                  to="/admin/login"
                  data-testid="footer-admin-link"
                  className="text-ink-dim hover:text-brand transition-colors"
                >
                  {t("footer.admin")}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-edge flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-eyebrow text-ink-dim">
          <span>© {year} C-Carpenter · {t("footer.rights")}</span>
          <span>London · United Kingdom</span>
        </div>
      </div>
    </footer>
  );
}
